import { useEffect, useState } from "react";
import PageHeader from "../components/shared/PageHeader";

type Tab = "health" | "costs" | "alerts" | "feedback" | "eval";

interface HealthData {
  corpus?: {
    by_kind: Array<{ kind: string; docs: number; chunks: number }>;
    total_chunks: number;
  };
  ingest_runs?: Array<Record<string, unknown>>;
  alert_events_7d?: Array<{ rule_id: string; events_7d: number; sent: number }>;
  insights?: { active_count: number; latest: string | null };
  sources?: Array<{
    source_id: string;
    display_name: string;
    slo_max_lag_hours: number;
  }>;
}

interface CostsData {
  by_model?: Array<{
    model_used: string;
    queries: number;
    cost_usd: number;
    avg_latency_ms: number;
  }>;
  by_class?: Array<{ question_class: string; n: number }>;
  by_confidence?: Array<{ confidence: string; n: number }>;
  cache?: { hits: number; total: number; rate: number };
  vectorize?: { enabled_count: number; total: number };
}

interface AlertsData {
  config?: Array<{
    rule_id: string;
    display_name: string;
    severity_threshold: string;
    is_enabled: number;
    min_gap_hours: number;
  }>;
  events?: Array<{
    rule_id: string;
    subject_id: string;
    severity: string;
    emitted_at: string;
    delivery_state: string;
  }>;
}

interface FeedbackData {
  flagged?: Array<Record<string, unknown>>;
}

interface EmptyTableFinding {
  table: string;
  display_name: string;
  status: "ok" | "empty" | "stale";
  row_count: number;
  latest_ts: string | null;
  staleness_hours_threshold: number;
  hours_since_latest: number | null;
  message: string;
}

interface EmptyTablesResponse {
  findings: EmptyTableFinding[];
  summary: {
    total_checked: number;
    flagged: number;
    empty: number;
    stale: number;
  };
}

interface EvalRunSummary {
  run_id: string;
  ts: string;
  total: number;
  passed: number;
  pass_block: number;
  pass_class: number;
  pass_confidence: number;
  avg_latency_ms: number | null;
}

interface EvalDetailRow {
  question_id: string;
  question_text: string;
  expected_class: string | null;
  actual_class: string | null;
  expected_blocked: number;
  actual_blocked: number;
  has_confidence_line: number;
  citation_count: number;
  latency_ms: number | null;
  status: string;
  pass_overall: number;
  error_message: string | null;
}

interface EvalLatestResponse {
  run_id: string | null;
  rows: EvalDetailRow[];
}

async function fetchJson<T>(url: string): Promise<T> {
  const r = await fetch(url, { credentials: "same-origin" });
  if (!r.ok) throw new Error(`${url} → ${r.status}`);
  return (await r.json()) as T;
}

export default function AdminAI() {
  const [tab, setTab] = useState<Tab>("health");
  const [health, setHealth] = useState<HealthData | null>(null);
  const [costs, setCosts] = useState<CostsData | null>(null);
  const [alerts, setAlerts] = useState<AlertsData | null>(null);
  const [feedback, setFeedback] = useState<FeedbackData | null>(null);
  const [emptyTables, setEmptyTables] = useState<EmptyTablesResponse | null>(
    null,
  );
  const [evalRuns, setEvalRuns] = useState<EvalRunSummary[] | null>(null);
  const [evalLatest, setEvalLatest] = useState<EvalLatestResponse | null>(null);
  const [evalRunning, setEvalRunning] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setErr(null);
    const loader = (() => {
      if (tab === "health" && !health) {
        return Promise.all([
          fetchJson<HealthData>("/api/admin/ai/health").then(setHealth),
          fetchJson<EmptyTablesResponse>("/api/admin/ai/empty-tables")
            .then(setEmptyTables)
            .catch(() => null),
        ]);
      }
      if (tab === "costs" && !costs)
        return fetchJson<CostsData>("/api/admin/ai/costs").then(setCosts);
      if (tab === "alerts" && !alerts)
        return fetchJson<AlertsData>("/api/admin/ai/alerts").then(setAlerts);
      if (tab === "feedback" && !feedback)
        return fetchJson<FeedbackData>("/api/ask/feedback/flagged").then(
          setFeedback,
        );
      if (tab === "eval" && !evalRuns) {
        return Promise.all([
          fetchJson<{ runs: EvalRunSummary[] }>("/api/admin/ai/eval/runs").then(
            (r) => setEvalRuns(r.runs ?? []),
          ),
          fetchJson<EvalLatestResponse>("/api/admin/ai/eval/latest").then(
            setEvalLatest,
          ),
        ]);
      }
      return null;
    })();
    if (loader)
      (loader as Promise<unknown>).catch((e: Error) => setErr(e.message));
  }, [tab, health, costs, alerts, feedback, evalRuns]);

  async function runEvalNow() {
    setEvalRunning(true);
    setErr(null);
    try {
      const r = await fetch("/api/admin/ai/eval/run", {
        method: "POST",
        credentials: "same-origin",
      });
      if (!r.ok) throw new Error(`run → ${r.status}`);
      const [runs, latest] = await Promise.all([
        fetchJson<{ runs: EvalRunSummary[] }>("/api/admin/ai/eval/runs").then(
          (d) => d.runs ?? [],
        ),
        fetchJson<EvalLatestResponse>("/api/admin/ai/eval/latest"),
      ]);
      setEvalRuns(runs);
      setEvalLatest(latest);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setEvalRunning(false);
    }
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <PageHeader
          title="Ember Platform Admin"
          subtitle="Observability for the /api/ask RAG stack, nightly cron, alert pipeline, ML outputs, and the local LLM call surface."
        />
      </div>

      <div className="flex gap-1 mb-6 border-b border-theme-subtle">
        {(["health", "costs", "alerts", "feedback", "eval"] as Tab[]).map(
          (t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`px-4 py-2 text-sm font-medium transition ${
                tab === t
                  ? "text-theme-primary border-b-2 border-theme-primary -mb-px"
                  : "text-theme-muted hover:text-theme-secondary"
              }`}
            >
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ),
        )}
      </div>

      {err && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 mb-4 text-sm">
          {err}
        </div>
      )}

      {tab === "health" && health && (
        <>
          {emptyTables && <EmptyTablesPanel data={emptyTables} />}
          <HealthTab data={health} />
        </>
      )}
      {tab === "costs" && costs && <CostsTab data={costs} />}
      {tab === "alerts" && alerts && <AlertsTab data={alerts} />}
      {tab === "feedback" && feedback && <FeedbackTab data={feedback} />}
      {tab === "eval" && (
        <EvalTab
          runs={evalRuns ?? []}
          latest={evalLatest}
          running={evalRunning}
          onRun={runEvalNow}
        />
      )}
    </div>
  );
}

function EmptyTablesPanel({ data }: { data: EmptyTablesResponse }) {
  const flagged = data.findings.filter((f) => f.status !== "ok");
  if (flagged.length === 0) {
    return (
      <Card
        title={`Producer health — ${data.summary.total_checked} tables, all current`}
      >
        <p className="text-xs text-theme-muted">
          No empty / stale tables. Nightly + weekly + monthly producers have
          written within their cadence.
        </p>
      </Card>
    );
  }
  return (
    <Card
      title={`Producer health — ${flagged.length} flagged (${data.summary.empty} empty, ${data.summary.stale} stale)`}
    >
      <Table
        caption="Data freshness by source table"
        head={["Table", "Status", "Rows", "Latest", "Threshold (h)", "Note"]}
        rows={flagged.map((f) => [
          f.display_name,
          f.status,
          f.row_count,
          f.latest_ts ? new Date(f.latest_ts).toLocaleString() : "—",
          f.staleness_hours_threshold,
          f.message,
        ])}
      />
    </Card>
  );
}

function EvalTab({
  runs,
  latest,
  running,
  onRun,
}: {
  runs: EvalRunSummary[];
  latest: EvalLatestResponse | null;
  running: boolean;
  onRun: () => void;
}) {
  return (
    <>
      <Card title="LLM eval — quality gate against the local Llama / gpt-oss stack">
        <p className="text-sm text-theme-secondary mb-3">
          Daily cron + on-demand. Runs a 10-question golden set through /api/ask
          and scores deterministic features: PII/out-of-scope blocking
          correctness, classifier accuracy, confidence-line presence, citation
          count, latency. Doesn't score answer-content quality (judge model is a
          follow-up).
        </p>
        <button
          type="button"
          onClick={onRun}
          disabled={running}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${
            running
              ? "bg-theme-overlay text-theme-muted cursor-not-allowed"
              : "bg-dcvfd text-white hover:bg-dcvfd-light"
          }`}
        >
          {running ? "Running… (30–90s)" : "Run eval now"}
        </button>
      </Card>

      <Card title={`Recent runs (${runs.length})`}>
        <Table
          caption="LLM eval runs with pass/block, class, confidence, and latency"
          head={[
            "Run",
            "When",
            "Pass",
            "Block",
            "Class",
            "Confidence",
            "Avg latency (ms)",
          ]}
          rows={runs.map((r) => [
            r.run_id.slice(0, 8),
            new Date(r.ts).toLocaleString(),
            `${r.passed}/${r.total}`,
            `${r.pass_block}/${r.total}`,
            `${r.pass_class}/${r.total}`,
            `${r.pass_confidence}/${r.total}`,
            r.avg_latency_ms ?? "—",
          ])}
        />
      </Card>

      {latest && latest.run_id && (
        <Card title={`Latest run detail — ${latest.run_id.slice(0, 8)}`}>
          <Table
            caption="Per-question eval results: expected vs actual block/class, confidence, citations, latency, and pass"
            head={[
              "Q",
              "Question",
              "Block exp/act",
              "Class exp/act",
              "Conf",
              "Cites",
              "Latency",
              "Pass",
            ]}
            rows={latest.rows.map((r) => [
              r.question_id,
              r.question_text.slice(0, 60),
              `${r.expected_blocked ? "Y" : "N"} / ${r.actual_blocked ? "Y" : "N"}`,
              `${r.expected_class ?? "—"} / ${r.actual_class ?? "—"}`,
              r.has_confidence_line ? "Y" : "N",
              r.citation_count,
              r.latency_ms ?? "—",
              r.pass_overall ? "✓" : "✗",
            ])}
          />
        </Card>
      )}
    </>
  );
}

function Card({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="glass rounded-xl p-5 mb-4">
      <h3 className="text-sm font-semibold text-theme-secondary mb-3">
        {title}
      </h3>
      {children}
    </div>
  );
}

function Table({
  head,
  rows,
  caption,
}: {
  head: string[];
  rows: Array<Array<string | number | null>>;
  /** Accessible table name; rendered visually hidden via <caption>. */
  caption?: string;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead>
          <tr className="text-xs text-theme-muted uppercase tracking-wide">
            {head.map((h) => (
              <th
                key={h}
                scope="col"
                className="text-left py-2 pr-4 font-medium"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="hover:bg-theme-overlay/50 transition-colors">
              {r.map((c, j) => (
                <td key={j} className="py-2 pr-4 text-theme-primary">
                  {c ?? "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function HealthTab({ data }: { data: HealthData }) {
  return (
    <>
      <Card
        title={`Knowledge corpus — ${data.corpus?.total_chunks ?? 0} chunks`}
      >
        <Table
          caption="Corpus document and chunk counts by kind"
          head={["Kind", "Docs", "Chunks"]}
          rows={(data.corpus?.by_kind ?? []).map((r) => [
            r.kind,
            r.docs,
            r.chunks,
          ])}
        />
      </Card>
      <Card title="Recent ingest runs">
        <Table
          head={[
            "Kind",
            "Status",
            "Started",
            "Docs new/upd/skip",
            "Chunks",
            "Embeddings",
            "Err",
          ]}
          rows={(data.ingest_runs ?? []).map((r) => [
            r.kind as string,
            r.status as string,
            r.started_at as string,
            `${r.docs_new}/${r.docs_updated}/${r.docs_skipped}`,
            r.chunks_upserted as number,
            r.embeddings_upserted as number,
            (r.error_message as string | null) ?? "",
          ])}
        />
      </Card>
      <Card title="Insights — active cards">
        <p className="text-sm text-theme-secondary">
          {data.insights?.active_count ?? 0} active cards, last generated{" "}
          <span className="font-mono">{data.insights?.latest ?? "never"}</span>.
        </p>
      </Card>
      <Card title="Alert events (7d)">
        <Table
          caption="Alert events and deliveries in the last 7 days, by rule"
          head={["Rule", "Events", "Sent"]}
          rows={(data.alert_events_7d ?? []).map((r) => [
            r.rule_id,
            r.events_7d,
            r.sent,
          ])}
        />
      </Card>
    </>
  );
}

function CostsTab({ data }: { data: CostsData }) {
  const totalCost = (data.by_model ?? []).reduce(
    (a, b) => a + (b.cost_usd ?? 0),
    0,
  );
  return (
    <>
      <Card title={`Spend by model — $${totalCost.toFixed(3)} total (30d)`}>
        <Table
          caption="LLM cost and latency by model"
          head={["Model", "Queries", "Cost ($)", "Avg latency (ms)"]}
          rows={(data.by_model ?? []).map((r) => [
            r.model_used,
            r.queries,
            r.cost_usd.toFixed(4),
            r.avg_latency_ms,
          ])}
        />
      </Card>
      <Card title="Question mix">
        <Table
          caption="Query count by question class"
          head={["Class", "Count"]}
          rows={(data.by_class ?? []).map((r) => [r.question_class, r.n])}
        />
      </Card>
      <Card title="Confidence distribution">
        <Table
          caption="Query count by confidence band"
          head={["Confidence", "Count"]}
          rows={(data.by_confidence ?? []).map((r) => [r.confidence, r.n])}
        />
      </Card>
      <Card title="Retrieval + cache">
        <p className="text-sm">
          Cache hit rate:{" "}
          {data.cache
            ? `${(data.cache.rate * 100).toFixed(1)}% (${data.cache.hits}/${data.cache.total})`
            : "—"}
          <br />
          Vectorize-assisted:{" "}
          {data.vectorize
            ? `${data.vectorize.enabled_count}/${data.vectorize.total}`
            : "—"}
        </p>
      </Card>
    </>
  );
}

function AlertsTab({ data }: { data: AlertsData }) {
  return (
    <>
      <Card title="Rule config">
        <Table
          caption="Alert rule configuration"
          head={["Rule", "Threshold", "Enabled", "Min gap (h)"]}
          rows={(data.config ?? []).map((r) => [
            r.display_name,
            r.severity_threshold,
            r.is_enabled ? "yes" : "no",
            r.min_gap_hours,
          ])}
        />
      </Card>
      <Card title="Recent events">
        <Table
          caption="Recent alert events"
          head={["Emitted", "Rule", "Subject", "Severity", "State"]}
          rows={(data.events ?? []).map((r) => [
            r.emitted_at,
            r.rule_id,
            r.subject_id,
            r.severity,
            r.delivery_state,
          ])}
        />
      </Card>
    </>
  );
}

function FeedbackTab({ data }: { data: FeedbackData }) {
  return (
    <Card title="Queries flagged by ≥2 down-votes">
      {(data.flagged ?? []).length === 0 ? (
        <p className="text-sm text-theme-muted">No flagged queries yet.</p>
      ) : (
        <Table
          caption="Flagged queries with down-votes"
          head={["Query ID", "When", "Class", "Model", "Down-votes", "Reasons"]}
          rows={(data.flagged ?? []).map((r: Record<string, unknown>) => [
            (r.query_id as string).slice(0, 8),
            r.queried_at as string,
            (r.question_class as string) ?? "",
            (r.model_used as string) ?? "",
            r.down_votes as number,
            (r.reason_raw as string) ?? "",
          ])}
        />
      )}
    </Card>
  );
}
